import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  CircularProgress,
  Button,
  Alert
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { API_URL } from '../config';

function VerifyEmail() {
  const [status, setStatus] = useState('pending');
  const [error, setError] = useState('');
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email;

  useEffect(() => {
    if (token && token.trim() !== '') {
      verifyEmail(token);
    }
  }, [token]);

  const verifyEmail = async (verificationToken) => {
    try {
      const response = await fetch(`${API_URL}/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: verificationToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Verification failed');
      }

      setStatus('success');
      setTimeout(() => {
        navigate('/signin');
      }, 3000);
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  };

  const resendVerification = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to resend verification email');
      }

      setStatus('resent');
    } catch (err) {
      setError(err.message);
    }
  };

  // If no token or empty token, show email verification instructions
  if (!token || token.trim() === '') {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom color="primary">
            Check Your Email
          </Typography>
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Verification email sent!</strong>
            </Typography>
            {email ? (
              <Typography variant="body2">
                We've sent a verification email to <strong>{email}</strong>. 
                Please check your inbox (and spam folder) and click the verification link to activate your account.
              </Typography>
            ) : (
              <Typography variant="body2">
                We've sent a verification email to your registered email address. 
                Please check your inbox (and spam folder) and click the verification link to activate your account.
              </Typography>
            )}
          </Alert>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Didn't receive the email? Check your spam folder{email ? ' or click the button below to resend' : ''}.
          </Typography>
          {email && (
            <>
              <Button 
                variant="outlined" 
                onClick={resendVerification}
                disabled={status === 'resent'}
                sx={{ mb: 2 }}
              >
                {status === 'resent' ? 'Email Resent!' : 'Resend Verification Email'}
              </Button>
              {status === 'resent' && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Verification email has been resent! Please check your inbox.
                </Alert>
              )}
            </>
          )}
          <Box sx={{ mt: 3 }}>
            <Button 
              variant="text" 
              onClick={() => navigate('/signin')}
            >
              Back to Sign In
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // If token exists, show verification status
  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        {status === 'pending' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {status === 'success' && (
          <>
            <Typography variant="h5" gutterBottom color="primary">
              Email Verified Successfully!
            </Typography>
            <Typography variant="body1">
              Redirecting to sign in page...
            </Typography>
          </>
        )}
        {status === 'error' && (
          <>
            <Typography variant="h5" gutterBottom color="error">
              Verification Failed
            </Typography>
            <Typography variant="body1" color="error">
              {error}
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/signup')}
              sx={{ mt: 2 }}
            >
              Back to Sign Up
            </Button>
          </>
        )}
      </Paper>
    </Container>
  );
}

export default VerifyEmail; 